import { useParams, Link, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  TrendingUp,
  FileText,
  MessageSquare,
  Calendar,
  Leaf,
  Globe,
  CheckCircle,
  Zap,
  Target,
  Layers,
  Users,
  Maximize2,
  X,
  GraduationCap,
  Shield,
  Search,
  Video,
  ShoppingBag,
  Bot,
  Cpu
} from "lucide-react";
import sopCycle from "@/assets/case-studies/sop-cycle.jpg";
import invoiceFlow from "@/assets/case-studies/invoice-flow.jpg";
import eventWorkflow from "@/assets/case-studies/event-workflow.jpg";
import chatbotFlow from "@/assets/case-studies/chatbot-flow.jpg";
import esgFlow from "@/assets/case-studies/esg-flow.jpg";
import pitchBotWorkflow from "@/assets/case-studies/pitch-bot-workflow.png";
import fmcgSopCycle from "@/assets/case-studies/fmcg-sop-cycle.png";
import lmsDrmWorkflow from "@/assets/case-studies/lms-drm-workflow.png";
import onpremDocAiFlow from "@/assets/case-studies/onprem-doc-ai-flow.png";
import complianceQaRagFlow from "@/assets/case-studies/compliance-qa-rag-flow.png";
import miningDetectionFlow from "@/assets/case-studies/mining-detection-flow.png";
import minemitraChatbotFlow from "@/assets/case-studies/minemitra-chatbot-flow.png";
import faceRecognitionFlow from "@/assets/case-studies/face-recognition-flow.png";
import equipmentOrderingFlow from "@/assets/case-studies/equipment-ordering-flow.png";
import concreteCuringFlow from "@/assets/case-studies/concrete-curing-flow.png";
import autonomousMaterialFlow from "@/assets/case-studies/autonomous-material-flow.png";
import cncTendingFlow from "@/assets/case-studies/cnc-tending-flow.png";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { AnimatedSection, FadeIn } from "@/components/AnimatedSection";
import SEO, { breadcrumbs } from "@/components/SEO";

const caseStudiesData = {
  "autonomous-material-movement": {
    id: "autonomous-material-movement",
    icon: Bot,
    title: "Autonomous Material Movement in Manufacturing",
    client: "Manufacturing & Intralogistics Enterprise",
    overview: "How a manufacturing facility automated internal material transport using Collaborative Robotics' Proxie platform, replacing repetitive cart pushing with intelligent, event-driven intralogistics.",
    challenge: {
      description: "Employees in a large manufacturing facility spent significant operational hours manually pushing carts of materials, parts, work-in-progress inventory, and empty containers across long distances. This created manpower dependencies, inconsistent station supply times, and production line starvation without altering physical facility infrastructure.",
      problems: [
        "Production workers spending hours walking across facilities to transport carts instead of focusing on manufacturing tasks",
        "Internal material delivery times dependent on manual runner availability and shift schedules",
        "Frequent line starvation as production stations waited for raw materials or container returns",
        "Inability to install fixed conveyor networks due to dynamic facility layouts and changing routing requirements"
      ]
    },
    solution: {
      description: "We deployed Collaborative Robotics' Proxie AMR platform to handle repetitive material transport routes autonomously. Integrated into the facility event stream, the robots respond dynamically to station replenishment requests and material readiness signals.",
      features: [
        "Autonomous mobile robot navigation designed to operate safely alongside human workforce",
        "Event-driven transport orchestration triggered by station status and cart readiness",
        "Dynamic obstacle avoidance and route optimization without facility layout modifications",
        "Centralized fleet monitoring layer providing real-time mission visibility and route utilization analytics"
      ]
    },
    implementation: [
      "Facility material-flow mapping and high-frequency route identification",
      "Proxie platform evaluation for navigation, safety, and cart compatibility",
      "Scoped pilot deployment across selected high-volume material routes",
      "Event-based workflow software integration for automated pickup and delivery signals",
      "Fleet status dashboard configuration, operational validation, and L1/L2 support transition"
    ],
    impact: {
      operational: [
        "Transferred repetitive cart transportation from human workers to autonomous mobile robots",
        "100% offline edge navigation stability preventing network-induced delivery stalls",
        "Established structured, predictable material delivery schedules across production stations"
      ],
      business: [
        "Substantially increased workforce productivity by refocusing staff on high-value manufacturing",
        "Reduced internal logistics sensitivity to staffing shortages and shift changeover gaps",
        "Eliminated major capital expense by operating within existing facility infrastructure"
      ],
      strategic: [
        "Created a scalable intralogistics automation blueprint for incremental route expansion",
        "Generated actionable operational analytics on material movement bottlenecks and station cycle times",
        "Improved facility ergonomics by reducing repetitive long-distance cart pushing"
      ]
    },
    techStack: ["Collaborative Robotics (Proxie)", "ROS", "Python", "AMR Fleet Management", "REST APIs", "Event-Driven Architecture"],
    customDiagram: autonomousMaterialFlow,
    tags: ["Autonomous Robotics", "Intralogistics", "AMR", "ROS", "Workflow Automation"],
    color: "bg-blue-50",
    iconColor: "text-[#243b7f]",
    accentColor: "#243b7f"
  },
  "cnc-machine-tending-automation": {
    id: "cnc-machine-tending-automation",
    icon: Cpu,
    title: "CNC Machine Tending & Automated Part Handling",
    client: "Industrial Components Manufacturer",
    overview: "How an industrial components manufacturer automated raw material loading, finished part handling, and quality verification using Standard Bots RO1 cobots and ROS-based orchestration.",
    challenge: {
      description: "A precision components facility relied on skilled operators to manually load raw parts into CNC machines, initiate machining cycles, and unload finished parts. Operator availability during shift changes, breaks, and labor shortages led to machine idle time and reduced output across expensive CNC equipment.",
      problems: [
        "High-value skilled operators spending hours on repetitive, manual component loading and unloading",
        "CNC machine utilization tied directly to continuous operator presence at the cell",
        "Production bottlenecks during shift changeovers, breaks, and manpower shortages",
        "Need to handle multiple SKU component variants without building rigid, single-part automation cells"
      ]
    },
    solution: {
      description: "We engineered a flexible robotic tending cell utilizing the Standard Bots RO1 cobot, integrated vision systems, and ROS-based orchestration. The system autonomously manages the full sequence from component identification to CNC loading and unloading.",
      features: [
        "Standard Bots RO1 robotic arm for precise component pickup, loading, and placement",
        "Computer vision and sensor integration verifying part availability, orientation, and grip integrity",
        "ROS-based control logic managing state synchronization, machine signals, and safety protocols",
        "Flexible cell architecture supporting multi-SKU component variants with rapid changeover"
      ]
    },
    implementation: [
      "Workflow assessment analyzing machine cycle times, payloads, and cell footprint",
      "Standard Bots RO1 platform evaluation and proof of concept validation",
      "Robotic cell design and custom end-effector selection for multi-part handling",
      "Vision system, sensor feedback, and ROS orchestration software development",
      "Controlled pilot validation, fail-safe exception logic testing, and operator training"
    ],
    impact: {
      operational: [
        "Achieved consistent machine cycle times independent of operator presence",
        "Integrated automatic sensor verification for component availability and grip safety",
        "Zero damage to precision components through repeatable robotic handling"
      ],
      business: [
        "Significantly increased CNC machine utilization and overall output capacity",
        "Reallocated skilled machinists to setup, quality inspection, and process optimization",
        "Eliminated production downtime caused by operator shift gaps and breaks"
      ],
      strategic: [
        "Established an agile collaborative robotics model scalable to additional CNC cells",
        "Avoided costly custom traditional automation by leveraging flexible cobot architecture",
        "Enhanced workplace ergonomics and safety by eliminating heavy repetitive handling"
      ]
    },
    techStack: ["Standard Bots (RO1)", "ROS", "Computer Vision", "Python", "PLC Interface", "Sensors & End-Effectors"],
    customDiagram: cncTendingFlow,
    tags: ["Collaborative Robotics", "CNC Machine Tending", "Computer Vision", "ROS", "Industrial Automation"],
    color: "bg-amber-50",
    iconColor: "text-amber-600",
    accentColor: "#d97706"
  },
  "concrete-curing-automation": {
    id: "concrete-curing-automation",
    icon: CheckCircle,
    title: "AI Vision Concrete Curing Automation",
    client: "Leading Infrastructure Firm",
    overview: "A WhatsApp AI agent powered by Vision-Language Models (VLM) automating site concrete curing verification across 100+ WhatsApp groups, replacing manual HQ photo reviews with instant in-chat Pass/Fail feedback.",
    challenge: {
      description: "Site engineers uploaded curing photos across 100+ active WhatsApp site groups, but no instant Pass/Fail feedback was given in chat. Verification relied on HQ operators manually inspecting hundreds of photos 3 times daily and logging status in Excel, causing severe delays and structural quality risks.",
      problems: [
        "Managing over 100 site WhatsApp groups with zero automated or instant feedback",
        "HQ operators manually reviewing site photos 3x daily per group",
        "Manual, disconnected Excel spreadsheet tracking",
        "Delayed detection of dry concrete surfaces endangering structural quality"
      ]
    },
    solution: {
      description: "We deployed a WhatsApp AI agent with a fine-tuned VLM that joins site groups, analyzes curing photos for moisture, hessian coverage, and ponding, delivers instant Pass/Fail feedback directly in chat, and auto-syncs live executive dashboards.",
      features: [
        "Automated photo monitoring across 100+ WhatsApp site groups",
        "Instant Pass/Fail feedback delivered directly in WhatsApp chats",
        "VLM visual analysis for moisture and hessian coverage",
        "Real-time executive compliance dashboard replacing Excel logs"
      ]
    },
    implementation: [
      "Curation of concrete curing visual datasets",
      "VLM prompt tuning for curing compliance classification",
      "Custom multi-group WhatsApp API engine",
      "Instant in-chat Pass/Fail feedback and anomaly alerts",
      "Live executive cloud dashboard rollout"
    ],
    impact: {
      operational: [
        "100% elimination of manual HQ Excel tracking across 100+ groups",
        "Instant in-chat Pass/Fail feedback delivered within seconds",
        "Zero delay in identifying dry or un-cured concrete"
      ],
      business: [
        "Prevented concrete strength non-compliance and rework",
        "Saved hundreds of HQ operator hours monthly",
        "Seamless adoption leveraging existing WhatsApp workflows"
      ],
      strategic: [
        "Standardized digital quality assurance across 100+ active sites",
        "Automated visual audit trail for compliance"
      ]
    },
    techStack: ["Custom WhatsApp API", "VLM", "Python", "FastAPI", "React.js", "PostgreSQL"],
    customDiagram: concreteCuringFlow,
    tags: ["Computer Vision", "VLM", "Construction", "Custom WhatsApp API", "Quality Assurance"],
    color: "bg-emerald-50",
    iconColor: "text-emerald-600"
  },
  "equipment-ordering-app": {
    id: "equipment-ordering-app",
    icon: ShoppingBag,
    title: "E-Commerce Ordering Platform for Site Equipment",
    client: "Leading Construction Group",
    overview: "How a leading construction firm digitized its equipment requisition process, replacing manual phone orders and adapting the workflow to project managers' preferences.",
    challenge: {
      description: "The company's site equipment ordering relied heavily on manual phone calls, written logs, and text messages from site supervisors. This process led to high error rates, duplicated rentals, and incorrect deliveries. To solve this, we initially built a robust project management dashboard, but field project managers strongly preferred using WhatsApp, leading to low adoption of the dashboard.",
      problems: [
        "High delivery error rates from verbal phone-based equipment orders",
        "Costly project downtime when incorrect equipment sizes arrived on site",
        "Low adoption of the complex project management dashboard by field supervisors",
        "Lack of centralized tracking for equipment rental periods and costs"
      ]
    },
    solution: {
      description: "We built a mobile-friendly e-commerce ordering catalog that replaced phone ordering. Listening to user feedback, we pivoted from the complex project management dashboard to a simplified pipeline integrated directly with the WhatsApp Business API for order status, updates, and approvals.",
      features: [
        "Mobile-first digital catalog for equipment browsing and ordering",
        "Automated order validation and inventory matching algorithms",
        "Integration with the WhatsApp Business API for order approvals and alerts",
        "Centralized procurement and tracking ledger for accounting"
      ]
    },
    implementation: [
      "User journey mapping with site supervisors and warehouse logisticians",
      "Mobile web e-commerce catalog development with real-time stock indicators",
      "Integration with existing warehouse management systems (WMS)",
      "Pivot from dashboard UI to direct WhatsApp notification workflows",
      "Rollout, adoption monitoring, and feedback collection loops"
    ],
    impact: {
      operational: [
        "100% elimination of wrong equipment delivery errors",
        "Substantially faster order approvals routed directly through WhatsApp",
        "Near-zero training required due to familiar messaging interfaces"
      ],
      business: [
        "Significant cost savings by preventing over-renting and idle equipment",
        "Reduced administrative overhead in managing log sheets and phone logs",
        "Minimised project timeline delays caused by wrong machine arrivals"
      ],
      strategic: [
        "Transitioned to a user-centric deployment model aligning with field habits",
        "Established a clean digital audit trail of site procurement costs",
        "Created a modular microservices template for future utility ordering apps"
      ]
    },
    techStack: ["React.js", "Python", "WhatsApp Business API", "FastAPI", "PostgreSQL", "Mobile Web"],
    customDiagram: equipmentOrderingFlow,
    tags: ["E-Commerce", "Procurement", "WhatsApp API", "Workflow Automation"],
    color: "bg-blue-50",
    iconColor: "text-blue-600",
    accentColor: "#2563eb"
  },
  "sovereign-mining-detection": {
    id: "sovereign-mining-detection",
    icon: Video,
    title: "Sovereign Mining Detection & ANPR System",
    client: "State Mining Departments",
    overview: "How a fully edge-deployed dual-camera barricade monitoring system utilizing YOLOv4-Tiny, DeepSORT, and PaddleOCR interdicts illegal mineral transits offline.",
    challenge: {
      description: "State checkpoints in remote highway locations faced high rates of unauthorized mineral transportation, permit mismatch fraud, and illegal over-loading. Constant power fluctuations and lack of stable internet connectivity made cloud-based computer vision APIs unusable. The client required a completely offline, sovereign edge system that acts in real-time.",
      problems: [
        "Unreliable network connectivity at remote forest and highway checkpoints",
        "High volume of vehicles carrying illicit or permit-mismatched mineral types",
        "Manual vehicle plate recording causing massive congestion and human tracking errors",
        "Sovereign data mandates preventing external data transmission to third-party APIs"
      ]
    },
    solution: {
      description: "We deployed a dual-camera edge computer vision pipeline. One specialized camera analyzes cargo/mineral types, while a high-speed camera performs License Plate Recognition (ANPR). All processing runs locally on secure hardware.",
      features: [
        "Mineral type recognition powered by YOLOv4-Tiny for real-time cargo categorization",
        "Localized PaddleOCR and DeepSORT vehicle license tracking and matching",
        "On-premise decision engine correlating vehicle permit data with detected cargo",
        "Instant local barricade auto-alerts triggering on mismatched loads"
      ]
    },
    implementation: [
      "Site survey, camera placement calibration, and rugged enclosure setup",
      "Model optimization for edge deployment under strict power limits",
      "Integration of the decision engine with local offline transit database syncs",
      "Barricade physical system integration and alert testing",
      "Field training and continuous localized model tuning"
    ],
    impact: {
      operational: [
        "98% successful illegal load interdiction rate at checked barricades",
        "100% offline edge inference ensuring zero network failure delays",
        "Automated license plate extraction under 200 milliseconds per vehicle"
      ],
      business: [
        "Recovered substantial tax revenue by stopping illegal mining mineral transport",
        "Eliminated checkpoint inspector bias and manual cataloging discrepancies",
        "Completely automated traffic logs and audit trail database creation"
      ],
      strategic: [
        "Sovereign state infrastructure protecting natural mineral assets",
        "Reusable, rugged edge computer vision template for other remote checkpoints",
        "Complete local custody of state transportation and logistics logs"
      ]
    },
    techStack: ["YOLOv4-Tiny", "DeepSORT", "PaddleOCR", "Python Backend", "Edge Compute", "Local DB"],
    customDiagram: miningDetectionFlow,
    tags: ["Edge AI", "Computer Vision", "ANPR", "Sovereign Infrastructure"],
    color: "bg-slate-50",
    iconColor: "text-[#243b7f]",
    accentColor: "#243b7f"
  },
  "minemitra-chatbot": {
    id: "minemitra-chatbot",
    icon: MessageSquare,
    title: "MineMitra: Cosine Similarity Mining Q&A Bot",
    client: "Sovereign Regulatory Authorities",
    overview: "How regulatory mining officers navigate state mining manuals and policies offline using a conversational bot matching natural language queries to vector indexes via cosine similarity.",
    challenge: {
      description: "Mining inspectors in remote checkpoints and field offices needed instant access to dense regulatory manuals, tax structures, and compliance books. Due to lack of public internet and strict sovereign classification of regulatory datasets, commercial cloud-hosted large language model APIs could not be utilized.",
      problems: [
        "Slow field lookup times of complex state regulatory documents by officers",
        "No network connectivity in remote check-posts to reach cloud compliance helpers",
        "Sovereign classification mandates preventing upload of regulations to public LLM systems",
        "High risk of text hallucination in critical compliance enforcement"
      ]
    },
    solution: {
      description: "We built MineMitra, an offline semantic-search conversational agent. By parsing policy manuals into pre-structured QA pairs and converting queries into embeddings locally, the chatbot matches intents to database vectors using cosine similarity with zero cloud calls.",
      features: [
        "Completely offline local SentenceTransformers text embedding pipelines",
        "Fast local Cosine Similarity index search against pre-structured regulatory manuals",
        "Deterministic QA matching to guarantee zero-hallucination compliance advice",
        "Clean, lightweight desktop interface for low-spec regulatory hardware"
      ]
    },
    implementation: [
      "Data formatting and indexing of state mining books into pre-structured QA datasets",
      "Development of the local embedding pipeline using lightweight transformers",
      "Vector index generation and cosine similarity lookup engine compilation",
      "Desktop GUI packaging and cross-platform installation tests",
      "Rollout to checkpoint workstations with offline database update scripts"
    ],
    impact: {
      operational: [
        "95% accuracy in direct compliance query lookup matching",
        "Under 100ms response time per query fully offline",
        "Zero network dependency allowing continuous regulatory enforcement"
      ],
      business: [
        "Reduced compliance query resolution time for checkpoint staff by 90%",
        "Standardized checkpoint enforcement interpretations across regional divisions",
        "Reduced administrative lookup errors and training cost"
      ],
      strategic: [
        "100% sovereign, self-contained regulatory knowledge base",
        "Data privacy shield securing government legal guidelines from external platforms",
        "Modular Q&A index framework supporting easy regulations updates"
      ]
    },
    techStack: ["SentenceTransformers", "Python", "Cosine Similarity Search", "SQLite", "Offline Desktop UI"],
    customDiagram: minemitraChatbotFlow,
    tags: ["Local RAG", "Embedding Search", "Compliance Bot", "Sovereign AI"],
    color: "bg-amber-50",
    iconColor: "text-amber-600",
    accentColor: "#d97706"
  },
  "face-recognition-attendance": {
    id: "face-recognition-attendance",
    icon: Users,
    title: "Biometric Face Recognition & Presence Monitoring",
    client: "Industrial Checkpoints",
    overview: "How biometric entry checkpoints and cabin safety lines deployed secure, fully local face detection and recognition utilizing RetinaNet and ArcFace.",
    challenge: {
      description: "Managing workforce attendance and cabin presence at remote, confidential construction sites suffered from buddy-punching, card loss, and slow check-ins. Due to corporate confidentiality, site location safety, and lack of external connectivity, cloud biometric platforms could not be utilized.",
      problems: [
        "Buddy-punching and identity verification fraud on entry/exit checkpoints",
        "Need to track active presence inside secure operating cabins for safety audits",
        "Confidential site data preventing use of third-party biometric cloud providers",
        "Harsh outdoor lighting conditions causing commercial readers to fail"
      ]
    },
    solution: {
      description: "We built an on-premise face recognition pipeline. It utilizes RetinaNet for fast local face detection and ArcFace to represent faces as 512-dimensional vector embeddings, matching them against local site databases with high precision.",
      features: [
        "RetinaNet face detection optimized for varying angles and lighting",
        "ArcFace encoding generating secure, un-reversible 512-dim face embeddings",
        "Multi-angle enrollment pipeline taking 10 reference images per person",
        "Entry/exit checkpoint logs + continuous cabin presence monitoring"
      ]
    },
    implementation: [
      "Edge terminal hardware setup and camera installation at checkpoints",
      "Development of the offline enroll app with 512-dimensional vector mapping",
      "Optimization of ArcFace embeddings matcher for edge CPU nodes",
      "Cabin continuous-loop tracking configuration and validation",
      "Deployment and local network database syncing setup"
    ],
    impact: {
      operational: [
        "Buddy-punching completely eliminated from checked locations",
        "99.7% biometric verification accuracy with under 300ms match speed",
        "Continuous cabin presence monitoring flagging safety compliance in real-time"
      ],
      business: [
        "30% reduction in attendance tracking overhead and card replacement costs",
        "Automated, tamper-proof logs for project compliance audits",
        "Eliminated recurring third-party cloud subscription fees"
      ],
      strategic: [
        "Biometric records stored fully locally under complete corporate custody",
        "Scalable biometric edge framework reusable on future remote worksites",
        "Enhanced security posture for confidential industrial projects"
      ]
    },
    techStack: ["RetinaNet", "ArcFace (512-dim)", "DeepSORT", "Python Backend", "Local SQLite DB"],
    customDiagram: faceRecognitionFlow,
    tags: ["Biometrics", "Face Detection", "ArcFace", "Edge AI"],
    color: "bg-purple-50",
    iconColor: "text-purple-600",
    accentColor: "#7c3aed"
  },
  "onprem-document-ai": {
    id: "onprem-document-ai",
    icon: Shield,
    title: "Sovereign Document AI & Fraud Detection API",
    client: "Leading National Retail Bank",
    overview: "How a Leading National Retail Bank deployed a secure, fully on-premise Document Processing API with automated pixel-level fraud analysis and KYC verification.",
    challenge: {
      description: "The bank faced significant regulatory compliance challenges and manual verification delays when onboarding new commercial and retail customers. Strict national data sovereignty and privacy regulations prohibited the use of cloud-based document extraction services. They needed an enterprise-grade solution that runs entirely in their local environment while detecting sophisticated digital document forgeries.",
      problems: [
        "Strict regulatory mandates preventing cloud data transfers for identity verification",
        "High rates of digital document manipulation (pixel-level editing) in application forms",
        "Manual validation of diverse KYC documents causing customer drop-outs",
        "High cost of manual document auditing and compliance logging"
      ]
    },
    solution: {
      description: "We built and deployed a fully containerized, sovereign Document AI API running on the bank's bare-metal infrastructure. The API processes structured and unstructured documents locally, validating authenticity and extracting user data securely.",
      features: [
        "Fully offline containerized deployment with zero-data-leak assurance",
        "Deep learning OCR optimized for low-contrast scans and multi-language layouts",
        "Active forgery detection scanning documents for pixel manipulation and metadata anomalies",
        "Biometric face-matching and automated database cross-referencing for KYC"
      ]
    },
    implementation: [
      "Offline security audits and local container architecture planning",
      "Model training and optimization for local CPU/GPU hardware constraints",
      "API integration with existing core banking directory and databases",
      "Rigorous penetration testing and data privacy verification",
      "Production rollout across secure regional data centers"
    ],
    impact: {
      operational: [
        "95% reduction in customer onboarding verification time",
        "99% detection rate of digitally modified or forged documents",
        "Zero data transmission outside the bank's private servers"
      ],
      business: [
        "80% savings in operational compliance and auditing overhead",
        "Significant boost in customer conversion rates due to instant processing",
        "Eliminated regulatory compliance fines related to KYC data handling"
      ],
      strategic: [
        "Standardized on-premise AI deployment framework for future modules",
        "Full alignment with strict national financial sovereignty regulations",
        "Complete ownership and security of user demographic and identity data"
      ]
    },
    techStack: ["Python", "FastAPI", "Docker / Kubernetes", "PyTorch", "OpenCV", "Bare-metal Hardware"],
    customDiagram: onpremDocAiFlow,
    tags: ["On-Premise", "Computer Vision", "Fraud Analysis", "Fintech"],
    color: "bg-blue-50",
    iconColor: "text-blue-600",
    accentColor: "#2563eb"
  },
  "compliance-qa-platform": {
    id: "compliance-qa-platform",
    icon: Search,
    title: "Conversational QA & Compliance Audit Platform",
    client: "Global Wealth Management Firm",
    overview: "How a Global Wealth Management Firm developed an internal conversational RAG tool to instantly query regulatory mandates, policy books, and SEC guidelines with full auditability.",
    challenge: {
      description: "With compliance guidelines changing continuously across multiple jurisdictions, compliance officers and portfolio managers spent hours manually scanning SEC updates, internal audit handbooks, and policy guidelines. This bottleneck delayed transaction approvals and increased the risk of regulatory non-compliance fines.",
      problems: [
        "Huge, unsearchable volume of regulatory manuals and policy booklets",
        "Slow transaction compliance checks leading to client frustration",
        "Risk of regulatory audits failing due to outdated guidelines references",
        "Inability to trace LLM answers back to authorized legal citations"
      ]
    },
    solution: {
      description: "We designed a secure conversational AI tool powered by a Retrieval-Augmented Generation (RAG) pipeline. This system processes regulatory documentation into vector databases, allowing staff to query rules via an interactive chat interface that guarantees precise source citations.",
      features: [
        "Ingestion pipelines for policy handbooks and live SEC regulatory feeds",
        "Highly secure vector database storing chunked semantic context",
        "Conversational QA assistant with strict rule-based prompt guardrails",
        "Source attribution showing exact document sections, pages, and dates"
      ]
    },
    implementation: [
      "Document ingestion and custom semantic chunking parser design",
      "Vector search optimization and hybrid retrieval model tuning",
      "Prompt engineering and guardrail implementation to prevent hallucinations",
      "User role permissions configuration and directory systems sync",
      "Internal pilot rollout and feedback-driven performance updates"
    ],
    impact: {
      operational: [
        "Auditing and policy lookup times cut by over 90%",
        "100% transparent citations for every compliance answer",
        "Automatic notifications for compliance teams on new SEC rule alerts"
      ],
      business: [
        "40% reduction in average compliance department search overhead",
        "Zero compliance or advisory audit violations since launch",
        "Faster client onboarding and transaction clearance times"
      ],
      strategic: [
        "Highly secure, sovereign cloud deployment protecting internal procedures",
        "Scalable framework ready to ingest global cross-border regulations",
        "Audit-ready digital logs of compliance advisory interactions"
      ]
    },
    techStack: ["React.js", "Python", "Vector Database", "LlamaIndex", "FastAPI", "Sovereign Cloud"],
    customDiagram: complianceQaRagFlow,
    tags: ["RAG Engine", "Fintech", "Compliance", "Semantic Search"],
    color: "bg-emerald-50",
    iconColor: "text-emerald-600",
    accentColor: "#059669"
  },
  "secure-lms-drm": {
    id: "secure-lms-drm",
    icon: GraduationCap,
    title: "Securing & Scaling Enterprise Learning",
    client: "Global Media & Education Conglomerate",
    overview: "How a Global Media & Education Conglomerate integrated secure DRM content protection, AI-driven question bank generation, and automated marketing into a centralized learning platform.",
    challenge: {
      description: "The client needed a secure, enterprise-grade learning system to train thousands of candidates while preventing IP theft of premium video course content. Additionally, marketing these courses, manually generating assessment questions, and tracking candidate performance was extremely resource-intensive.",
      problems: [
        "High risk of digital content piracy and unauthorized sharing of premium materials",
        "Time-consuming manual creation of assessment question banks from course videos",
        "Lack of automated, high-reach marketing tools to promote courses at scale",
        "Fragmented candidate data with no unified view of learning progress"
      ]
    },
    solution: {
      description: "We built an integrated, secure LMS platform featuring robust DRM protection, AI-driven question bank generation, automated marketing outreach pipelines, and an interactive candidate performance dashboard.",
      features: [
        "Multi-platform Digital Rights Management (DRM) with dynamic watermarking",
        "AI-powered natural language processing to generate diverse question banks",
        "Automated mass media marketing engine using dynamic ad templates",
        "Real-time candidate performance dashboard with predictive progress tracking"
      ]
    },
    implementation: [
      "Discovery & DRM framework architecture design",
      "AI question generation pipeline integration",
      "Mass media marketing and notification automation",
      "Interactive dashboard development and analytics setup",
      "Phased pilot deployment and security penetration testing"
    ],
    impact: {
      operational: [
        "Zero content leaks since deployment via robust DRM",
        "80% reduction in time spent creating course assessments",
        "Fully automated marketing campaign generation"
      ],
      business: [
        "Significant increase in course registration rates",
        "Reduced operational overhead for course administrators",
        "High user satisfaction score for learning insights"
      ],
      strategic: [
        "Secure, scalable platform ready for global deployment",
        "Centralized data model of learner performance",
        "Stronger competitive advantage in proprietary content protection"
      ]
    },
    techStack: ["React.js", "Python", "DRM", "Generative AI", "FastAPI", "PostgreSQL"],
    customDiagram: lmsDrmWorkflow,
    tags: ["LMS", "DRM", "Generative AI", "Analytics"],
    color: "bg-cyan-50",
    iconColor: "text-cyan-600",
    accentColor: "#0891b2"
  },
  "fmcg-sop-digitization": {
    id: "fmcg-sop-digitization",
    icon: BarChart3,
    title: "Digitizing S&OP for a Leading FMCG Giant",
    client: "Leading FMCG Company in North America",
    overview: "How a Fast-Moving Consumer Goods Company Replaced Manual Planning with a Scalable, Data-Driven S&OP Platform",

    challenge: {
      description: "The company's S&OP process relied on fragmented spreadsheets, distributor reports, and manual coordination across regions. With thousands of SKUs and multi-layer distribution, teams spent significant time reconciling data leading to delays, errors, and reactive decision-making.",
      problems: [
        "Fragmented S&OP data across Excel files and regional reports",
        "Time-intensive manual data consolidation and validation",
        "No real-time visibility into demand and stock movement",
        "Judgment-driven forecasts causing overstocking or stockouts",
        "No single source of truth for scenario analysis"
      ]
    },

    solution: {
      description: "We built a centralized, cloud-based S&OP platform for high-volume, multi-channel FMCG planning unifying data, automating workflows, and introducing ML-powered forecasting across all distribution levels.",
      features: [
        "Automated consolidation of actuals, forecasts, and stock data",
        "Multi-level sales visibility (primary, secondary, tertiary)",
        "ML-powered demand prediction across distribution layers",
        "Real-time dashboards with audit trails and governance",
        "Scenario planning for demand, supply, and promotions"
      ]
    },

    implementation: [
      "Discovery & cross-functional workflow mapping",
      "Cloud-native architecture design",
      "Core modules with automated data pipelines",
      "ML model development & retraining pipelines",
      "Testing, rollout & team adoption"
    ],

    impact: {
      operational: [
        "Eliminated manual reconciliation",
        "Improved data accuracy across channels",
        "Reduced stockouts and excess inventory"
      ],
      business: [
        "Unified sales, supply chain & finance teams",
        "Faster decision-making via real-time dashboards",
        "Data-driven forecast accuracy"
      ],
      strategic: [
        "Scalable platform for growth & expansion",
        "Stronger distributor relationships",
        "AI-ready data foundation"
      ]
    },

    techStack: ["Python", "React.js", "Cloud", "Machine Learning", "RabbitMQ"],
    customDiagram: fmcgSopCycle,
    tags: ["S&OP", "FMCG", "Machine Learning", "Demand Forecasting"],
    color: "bg-indigo-50",
    iconColor: "text-indigo-600",
    accentColor: "#4f46e5"
  },

  "sales-operations-digitization": {
    id: "sales-operations-digitization",
    icon: TrendingUp,
    title: "Sales & Operations Digitization",
    client: "Global Smartphone Brand",
    overview: "Digitizing Sales and Operations Management for a global smartphone brand through an end-to-end system that transformed fragmented Excel-based processes into a centralized, AI-ready platform.",

    challenge: {
      description: "The client's Sales & Operations Planning (S&OP) processes were spread across hundreds of Excel files with no single source of truth. Manual reconciliation was causing significant delays and errors, while decision-makers lacked the visibility needed for confident planning.",
      problems: [
        "Fragmented workflows across spreadsheets",
        "Time-consuming manual data handling",
        "High error rates and version conflicts",
        "Inability to run quick scenario analysis",
        "Poor visibility for decision-making"
      ]
    },

    solution: {
      description: "We built a centralized web-based S&OP platform with automated data consolidation, single source dashboards with audit trails, and an AI-ready structured data foundation.",
      features: [
        "Centralized web-based S&OP platform",
        "Automated data consolidation (actuals, forecasts, stock)",
        "Single source dashboards with audit trails",
        "AI-ready structured data foundation",
        "Real-time scenario analysis capabilities"
      ]
    },

    implementation: [
      "System design & workflow mapping",
      "Platform development with automated data flows",
      "Integration with existing data sources",
      "Testing and enterprise deployment",
      "Training and change management"
    ],

    impact: {
      operational: [
        "Eliminated manual reconciliation",
        "Reduced errors significantly",
        "Faster reporting cycles"
      ],
      business: [
        "Better cross-team alignment",
        "Faster and confident decision-making",
        "Standardized workflows"
      ],
      strategic: [
        "Scalable platform",
        "Improved inventory efficiency",
        "AI-ready data ecosystem"
      ]
    },

    techStack: ["Python", "React.js", "AWS", "OCR/ML Models"],
    customDiagram: sopCycle,
    tags: ["S&OP", "Digital Transformation", "Analytics", "Enterprise"],
    color: "bg-blue-50",
    iconColor: "text-[#243b7f]",
    accentColor: "#243b7f"
  },

  "ai-invoicing-engine": {
    id: "ai-invoicing-engine",
    icon: FileText,
    title: "AI Invoicing Engine",
    client: "Global Enterprise",
    overview: "AI-powered invoicing system to automate financial workflows for a global enterprise handling massive volumes of invoices in multiple formats and languages.",

    challenge: {
      description: "The client faced bottlenecks processing a massive volume of invoices arriving in multiple formats and languages. Manual processing led to errors in data entry, slow payment cycles, and compliance risks.",
      problems: [
        "Unstructured invoice formats",
        "High manual effort and processing time",
        "Payment delays and penalties",
        "Compliance and audit risks",
        "No scalable processing solution"
      ]
    },

    solution: {
      description: "We developed an Intelligent Document Processing (IDP) platform with AI-based data extraction, unified ingestion system, and API-driven workflow automation.",
      features: [
        "Intelligent Document Processing (IDP) platform",
        "AI-based data extraction",
        "Unified ingestion system for all formats",
        "API-driven workflow automation",
        "Multi-language support"
      ]
    },

    implementation: [
      "Workflow analysis & architecture design",
      "AI engine development and training",
      "ERP integrations",
      "Performance testing and optimization",
      "Phased rollout across departments"
    ],

    impact: {
      operational: [
        "Eliminated invoice backlog",
        "Faster processing times",
        "Reduced data entry errors"
      ],
      business: [
        "Improved financial visibility",
        "Faster payment cycles",
        "Reduced late payment penalties"
      ],
      strategic: [
        "Scalable financial system",
        "Automation-ready workflows",
        "Enhanced compliance posture"
      ]
    },

    techStack: ["Python", "React.js", "AWS", "OCR/ML Models"],
    customDiagram: invoiceFlow,
    tags: ["IDP", "Computer Vision", "Finance Automation", "AI/ML"],
    color: "bg-amber-50",
    iconColor: "text-amber-600",
    accentColor: "#d97706"
  },

  "event-brief-automation": {
    id: "event-brief-automation",
    icon: Calendar,
    title: "Event Brief Automation",
    client: "Events Management Company",
    overview: "AI system to automate event brief creation and approvals, transforming manual document-based workflows into streamlined, intelligent processes.",

    challenge: {
      description: "The client struggled with manual document-based workflows for event briefs, leading to slow approvals, lack of structured data, fragmented inputs, and inconsistent accuracy.",
      problems: [
        "Manual document-based workflows",
        "Slow approval processes",
        "Fragmented inputs from multiple sources",
        "Repetitive manual processes",
        "Inconsistent accuracy and no visibility"
      ]
    },

    solution: {
      description: "We developed an AI-powered brief generator with document parsing & field extraction, role-based workflows, and export-ready reports.",
      features: [
        "AI-powered brief generator",
        "Document parsing & field extraction",
        "Role-based approval workflows",
        "Export-ready professional reports",
        "Centralized brief management"
      ]
    },

    implementation: [
      "Workflow design and mapping",
      "AI engine development",
      "Role-based access system",
      "Azure cloud deployment",
      "User training and rollout"
    ],

    impact: {
      operational: [
        "70% faster brief generation",
        "Significantly reduced errors",
        "Streamlined approvals"
      ],
      business: [
        "Standardized workflows",
        "Faster execution cycles",
        "Improved client satisfaction"
      ],
      strategic: [
        "Scalable platform",
        "Strong data foundation",
        "Template for future automation"
      ]
    },

    techStack: ["Python", "React.js", "Azure", "NLP Models"],
    customDiagram: eventWorkflow,
    tags: ["NLP", "Document AI", "Workflow Automation", "Azure"],
    color: "bg-purple-50",
    iconColor: "text-purple-600",
    accentColor: "#7c3aed"
  },

  "whatsapp-ai-pitch-bot": {
    id: "whatsapp-ai-pitch-bot",
    icon: MessageSquare,
    title: "WhatsApp AI Pitch Bot",
    client: "Startup Ecosystem",
    overview: "AI-powered WhatsApp bot enabling regional founders to create professional pitch decks, breaking language barriers and democratizing access to startup resources.",

    challenge: {
      description: "Non-English speaking founders faced significant barriers in the English-first startup ecosystem. There was a lack of accessible resources for rural and regional entrepreneurs, with no scalable solution to bridge this gap.",
      problems: [
        "Language barrier for non-English founders",
        "English-first startup ecosystem",
        "Low accessibility for rural founders",
        "Lack of accessible startup resources",
        "No scalable solution for diverse regions"
      ]
    },

    solution: {
      description: "We built an AI-powered WhatsApp bot with multilingual input processing, guided pitch deck generation, and serverless scalable architecture to democratize founder enablement.",
      features: [
        "AI-powered WhatsApp bot interface",
        "Multilingual input processing",
        "Guided pitch deck generation",
        "Serverless scalable architecture",
        "Voice and text input support"
      ]
    },

    implementation: [
      "Founder journey mapping",
      "Conversational flow design",
      "AI multimedia processing integration",
      "WhatsApp Business API integration",
      "Phased rollout across regions"
    ],

    impact: {
      operational: [
        "Seamless bot performance",
        "Rapid user adoption",
        "High engagement rates"
      ],
      business: [
        "Massive onboarding of founders",
        "Ecosystem growth acceleration",
        "Democratized access to resources"
      ],
      strategic: [
        "Scalable nationwide solution",
        "Increased accessibility",
        "Model for replication"
      ]
    },

    techStack: ["Python", "WhatsApp Business API", "Serverless", "Generative AI"],
    customDiagram: pitchBotWorkflow,
    tags: ["Generative AI", "WhatsApp API", "Multilingual", "Chatbot"],
    color: "bg-emerald-50",
    iconColor: "text-emerald-600",
    accentColor: "#059669"
  },

  "esg-reporting-automation": {
    id: "esg-reporting-automation",
    icon: Leaf,
    title: "ESG Reporting Automation",
    client: "Enterprise Corporation",
    overview: "AI platform for automated ESG report generation, solving complex reporting requirements with intelligent content extraction and automated validation.",

    challenge: {
      description: "The client faced complex ESG reporting requirements with data scattered across multiple files. Manual compilation was time-consuming with high error risk, threatening compliance.",
      problems: [
        "Complex ESG reporting requirements",
        "Data scattered across files and systems",
        "Time-consuming manual compilation",
        "High error risk in reporting",
        "Compliance deadline pressures"
      ]
    },

    solution: {
      description: "We built an AI-based ESG report generator with intelligent content extraction, automated validation & scoring, and a central dashboard for oversight.",
      features: [
        "AI-based ESG report generator",
        "Intelligent content extraction",
        "Automated validation & scoring",
        "Central dashboard for oversight",
        "Compliance framework alignment"
      ]
    },

    implementation: [
      "ESG framework design",
      "AI pipeline development",
      "Workflow automation setup",
      "AWS cloud deployment",
      "Stakeholder training"
    ],

    impact: {
      operational: [
        "70% faster report generation",
        "Improved accuracy",
        "Reduced manual effort"
      ],
      business: [
        "Standardized ESG processes",
        "Transparent reporting",
        "Better stakeholder communication"
      ],
      strategic: [
        "Compliance-ready system",
        "Analytics-ready data",
        "Sustainable reporting foundation"
      ]
    },

    techStack: ["Python", "React.js", "AWS", "NLP/ML Models"],
    customDiagram: esgFlow,
    tags: ["ESG", "Document AI", "Compliance", "Sustainability"],
    color: "bg-teal-50",
    iconColor: "text-teal-600",
    accentColor: "#0d9488"
  },

  "ai-bilingual-chatbot": {
    id: "ai-bilingual-chatbot",
    icon: Globe,
    title: "AI Bilingual Chatbot",
    client: "Customer Service Platform",
    overview: "AI chatbot enabling multilingual customer support with English and Hindi language capabilities, dramatically improving response times and customer satisfaction.",

    challenge: {
      description: "The client relied on manual customer support systems with no multilingual capability. Response times were slow, support channels were fragmented, and scalability was limited.",
      problems: [
        "Manual customer support systems",
        "No multilingual capability",
        "Slow response times",
        "Fragmented support channels",
        "Limited scalability"
      ]
    },

    solution: {
      description: "We developed an AI-powered chatbot with English and Hindi support, real-time responses, and an admin dashboard for analytics and oversight.",
      features: [
        "AI-powered conversational chatbot",
        "English + Hindi language support",
        "Real-time response generation",
        "Admin dashboard for analytics",
        "Seamless human handoff"
      ]
    },

    implementation: [
      "Conversation design and flow mapping",
      "API-based chatbot development",
      "Dashboard & analytics setup",
      "Cloud deployment",
      "Continuous improvement pipeline"
    ],

    impact: {
      operational: [
        "60% faster query resolution",
        "Reduced manual workload",
        "24/7 availability"
      ],
      business: [
        "Improved customer experience",
        "Scalable support system",
        "Higher satisfaction scores"
      ],
      strategic: [
        "Multilingual reach expansion",
        "Insight-driven improvements",
        "Foundation for more languages"
      ]
    },

    techStack: ["Python", "React.js", "Cloud", "NLP/Chatbot Framework"],
    customDiagram: chatbotFlow,
    tags: ["NLP", "Chatbot", "Multilingual", "Customer Support"],
    color: "bg-rose-50",
    iconColor: "text-rose-600",
    accentColor: "#e11d48"
  }
};

// Get adjacent case studies for navigation
const caseStudyIds = Object.keys(caseStudiesData);

export default function CaseStudyDetailPage() {
  const { id } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const caseStudy = caseStudiesData[id];

  if (!caseStudy) {
    return <Navigate to="/case-studies" replace />;
  }

  const currentIndex = caseStudyIds.indexOf(id);
  const prevStudy = currentIndex > 0 ? caseStudiesData[caseStudyIds[currentIndex - 1]] : null;
  const nextStudy = currentIndex < caseStudyIds.length - 1 ? caseStudiesData[caseStudyIds[currentIndex + 1]] : null;

  const IconComponent = caseStudy.icon;
  const csOverview = (caseStudy.overview || "").replace(/Vanco(\s+AI)?/g, "VANCO");
  const csDescription = csOverview.length > 158 ? csOverview.substring(0, 155).replace(/\s+\S*$/, "") + "..." : csOverview;

  return (
    <div className="overflow-hidden" data-testid="case-study-detail-page">
      {/* Hero Section */}

      <section className="relative pt-32 pb-16 bg-gradient-to-br from-white via-slate-50 to-blue-50">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#243b7f]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[#f7c550]/10 rounded-full blur-3xl" />
        </div>
        <SEO
          title={`${caseStudy.title} | VANCO Case Study`}
          description={csDescription}
          path={`/case-studies/${id}`}
          type="article"
          jsonLd={breadcrumbs([
            { name: "Home", path: "/" },
            { name: "Case Studies", path: "/case-studies" },
            { name: caseStudy.title, path: `/case-studies/${id}` },
          ])}
        />

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          {/* Back link */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="mb-8"
          >
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-2 text-slate-600 hover:text-[#243b7f] transition-colors"
              data-testid="back-to-case-studies"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Case Studies</span>
            </Link>
          </motion.div>

          <div className="grid lg:grid-cols-5 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-3"
            >
              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-6">
                {caseStudy.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="bg-[#243b7f]/10 text-[#243b7f] text-xs font-medium">
                    {tag}
                  </Badge>
                ))}
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#0f172a] font-['Manrope'] mb-4">
                {caseStudy.title}
              </h1>

              <p className="text-slate-500 font-medium mb-6">{caseStudy.client}</p>

              <p className="text-xl text-slate-600 leading-relaxed mb-10">
                {caseStudy.overview}
              </p>

              {/* Added Tech Stack and CTA on the left column */}
              <div className="mb-10">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">Tech Stack</h3>
                <div className="flex flex-wrap gap-2 mb-8">
                  {caseStudy.techStack.map((tech) => (
                    <span
                      key={tech}
                      className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-[#243b7f] shadow-sm hover:shadow-md transition-shadow"
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                <Link to="/contact">
                  <Button
                    size="lg"
                    className="bg-[#243b7f] hover:bg-[#1e3a8a] text-white rounded-full px-8 py-6 text-lg"
                    data-testid="case-study-contact-cta-left"
                  >
                    Discuss Your Project
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Quick Stats & Diagram Card */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="lg:col-span-2"
            >
              <Card className="bg-white shadow-xl border-0">
                <CardContent className="p-6">
                  {caseStudy.customDiagram && (
                    <div className="relative group cursor-zoom-in" onClick={() => setIsModalOpen(true)}>
                      <div className="rounded-xl overflow-hidden border border-slate-100 shadow-sm transition-all duration-300 group-hover:shadow-md group-hover:border-slate-300">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest p-2 bg-slate-50 text-center border-b border-slate-100 italic">
                          {caseStudy.id === "sales-operations-digitization" ? "Process Framework" : "Process Visualization"}
                        </p>
                        <img
                          src={caseStudy.customDiagram}
                          alt="Process Visualization"
                          className="w-full h-auto"
                        />
                      </div>
                      <div className="absolute top-2 right-2 p-1.5 bg-white/80 backdrop-blur-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                        <Maximize2 className="w-3.5 h-3.5 text-slate-600" />
                      </div>
                    </div>
                  )}

                  {!caseStudy.customDiagram && (
                    <div className="text-center py-10 px-6">
                      <p className="text-slate-400 text-sm italic">Industry-standard implementation approach applied to this project.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Challenge & Solution */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Challenge */}
            <AnimatedSection>
              <Card className="h-full border-l-4 border-l-red-400 border-t-0 border-r-0 border-b-0 bg-red-50/30">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-red-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-[#0f172a] font-['Manrope']">The Challenge</h2>
                  </div>
                  <p className="text-slate-600 leading-relaxed mb-6">
                    {caseStudy.challenge.description}
                  </p>
                  <div className="space-y-3">
                    {caseStudy.challenge.problems.map((problem, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="w-2 h-2 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                        <span className="text-slate-700">{problem}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </AnimatedSection>

            {/* Solution */}
            <AnimatedSection delay={0.1}>
              <Card className="h-full border-l-4 border-l-emerald-400 border-t-0 border-r-0 border-b-0 bg-emerald-50/30">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <Target className="w-5 h-5 text-emerald-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-[#0f172a] font-['Manrope']">The Solution</h2>
                  </div>
                  <p className="text-slate-600 leading-relaxed mb-6">
                    {caseStudy.solution.description}
                  </p>
                  <div className="space-y-3">
                    {caseStudy.solution.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Implementation */}
      <section className="py-20 bg-gradient-to-br from-white via-slate-50 to-blue-50">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-[#243b7f]/5 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <AnimatedSection>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#243b7f]/10 flex items-center justify-center">
                  <Layers className="w-5 h-5 text-[#243b7f]" />
                </div>
                <h2 className="text-3xl font-bold text-[#0f172a] font-['Manrope']">Implementation</h2>
              </div>
            </div>

            <div className="grid md:grid-cols-5 gap-8">
              {caseStudy.implementation.map((step, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="relative"
                >
                  <Card className="h-full bg-white border-slate-100 hover:shadow-lg transition-shadow">
                    <CardContent className="p-6 text-center">
                      <div className="w-10 h-10 rounded-full bg-[#243b7f] text-white flex items-center justify-center mx-auto mb-4 font-bold">
                        {idx + 1}
                      </div>
                      <p className="text-slate-700 text-sm font-medium">{step}</p>
                    </CardContent>
                  </Card>
                  {idx < caseStudy.implementation.length - 1 && (
                    <div className="hidden lg:flex absolute top-1/2 -right-4 transform -translate-y-1/2 z-10 items-center justify-center translate-x-1/2">
                      <ArrowRight className="w-5 h-5 text-[#243b7f]/40" strokeWidth={3} />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Impact */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <AnimatedSection>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#f7c550]/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#f7c550]" />
                </div>
                <h2 className="text-3xl font-bold text-[#0f172a] font-['Manrope']">Impact & Results</h2>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Operational */}
              <Card className="bg-blue-50/50 border-0">
                <CardContent className="p-8">
                  <h3 className="text-lg font-bold text-[#243b7f] mb-4 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#243b7f]" />
                    Operational Impact
                  </h3>
                  <ul className="space-y-3">
                    {caseStudy.impact.operational.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-[#243b7f] mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Business */}
              <Card className="bg-amber-50/50 border-0">
                <CardContent className="p-8">
                  <h3 className="text-lg font-bold text-amber-700 mb-4 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-amber-600" />
                    Business Impact
                  </h3>
                  <ul className="space-y-3">
                    {caseStudy.impact.business.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Strategic */}
              <Card className="bg-emerald-50/50 border-0">
                <CardContent className="p-8">
                  <h3 className="text-lg font-bold text-emerald-700 mb-4 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-600" />
                    Strategic Impact
                  </h3>
                  <ul className="space-y-3">
                    {caseStudy.impact.strategic.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-12 bg-slate-50 border-t border-slate-100">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="flex justify-between items-center">
            {prevStudy ? (
              <Link
                to={`/case-studies/${prevStudy.id}`}
                className="flex items-center gap-3 text-slate-600 hover:text-[#243b7f] transition-colors group"
              >
                <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                <div>
                  <p className="text-xs text-slate-400 mb-1">Previous</p>
                  <p className="font-medium">{prevStudy.title}</p>
                </div>
              </Link>
            ) : <div />}

            {nextStudy ? (
              <Link
                to={`/case-studies/${nextStudy.id}`}
                className="flex items-center gap-3 text-slate-600 hover:text-[#243b7f] transition-colors group text-right"
              >
                <div>
                  <p className="text-xs text-slate-400 mb-1">Next</p>
                  <p className="font-medium">{nextStudy.title}</p>
                </div>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            ) : <div />}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#243b7f]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <FadeIn>
            <h2 className="text-3xl md:text-4xl font-bold text-white font-['Manrope'] mb-4">
              Ready to achieve similar results?
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Let's discuss how we can help transform your business with AI-powered solutions.
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-[#f7c550] hover:bg-[#eab308] text-[#0f172a] rounded-full px-10 py-6 text-lg font-bold transition-all duration-300 hover:scale-105"
              >
                Start Your Project
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* Image Modal Integration */}
      <AnimatePresence>
        {isModalOpen && caseStudy.customDiagram && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 md:p-8"
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative max-w-[95%] max-h-[90vh] bg-white rounded-3xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute top-4 right-4 z-10">
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-full bg-white/20 hover:bg-white/40 border-0 text-white backdrop-blur-md"
                  onClick={() => setIsModalOpen(false)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="font-bold text-[#243b7f] font-['Manrope']">
                  {caseStudy.title} - {caseStudy.id === "sales-operations-digitization" ? "Process Framework" : "Solution Architecture"}
                </h3>
              </div>

              <div className="overflow-auto max-h-[calc(90vh-60px)] p-2 md:p-6 bg-white">
                <img
                  src={caseStudy.customDiagram}
                  alt="Expanded Visualization"
                  className="w-full h-auto rounded-xl"
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
